// SchedulePage.js
import React, { useState, useEffect } from 'react';
import '../styles/SchedulePage.css';
import { Link } from 'react-router-dom';

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABELS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAY_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Generate time slots from 00:00 to 23:00 (every hour)
const TIME_SLOTS = Array.from({ length: 24 }, (_, i) => 
  `${i.toString().padStart(2, '0')}:00`
);

const toMinutes = (t) => {
  if (!t || typeof t !== "string" || !t.includes(":")) return Number.POSITIVE_INFINITY;
  const [h, m] = t.split(":").map((n) => parseInt(n, 10));
  return (Number.isFinite(h) ? h : 0) * 60 + (Number.isFinite(m) ? m : 0);
};

const normalizeScheduleData = (data) => {
  const base = Object.fromEntries(DAYS.map((d) => [d, []]));

  if (!data) return base;

  if (Array.isArray(data)) {
    for (const item of data) {
      const day = String(item?.dayOfWeek || "").toLowerCase();
      if (base[day]) base[day].push(item);
    }
  } else if (typeof data === "object") {
    for (const day of DAYS) {
      base[day] = Array.isArray(data[day]) ? data[day] : [];
    }
  }

  for (const day of DAYS) {
    base[day] = base[day]
      .map((s, idx) => ({
        id: s?.id ?? `${day}-${s?.startTime ?? "?"}-${s?.title ?? "show"}-${idx}`,
        backgroundColor: s?.backgroundColor || null,
        ...s,
      }))
      .sort((a, b) => toMinutes(a.startTime) - toMinutes(b.startTime));
  }

  return base;
};

const useScheduleData = () => {
  const [scheduleData, setScheduleData] = useState(() =>
    Object.fromEntries(DAYS.map((d) => [d, []]))
  );
  const [jukeboxConfig, setJukeboxConfig] = useState({
    title: "Jukebox",
    art: "🎵",
    image: null,
    description: "Auto DJ - Playing the best music mix",
    backgroundColor: null
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const url = `${process.env.PUBLIC_URL}/schedule.json`;
        const res = await fetch(url, { cache: "no-store" });
        if (!res.ok) throw new Error(`Failed to load schedule.json (${res.status})`);

        const data = await res.json();
        const normalized = normalizeScheduleData(data.shows || data);

        // Load jukebox config if available
        if (data.jukebox) {
          setJukeboxConfig({
            title: data.jukebox.title || "Jukebox",
            art: data.jukebox.art || "🎵",
            image: data.jukebox.image || null,
            description: data.jukebox.description || "Auto DJ - Playing the best music mix",
            backgroundColor: data.jukebox.backgroundColor || null
          });
          console.log("Jukebox config loaded:", data.jukebox); // Debug log
        }

        if (!cancelled) setScheduleData(normalized);
      } catch (e) {
        console.error("Schedule load error:", e);
        if (!cancelled) setScheduleData(Object.fromEntries(DAYS.map((d) => [d, []])));
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { scheduleData, jukeboxConfig, isLoading };
};

// Get show for a specific time slot on a specific day
const getShowForTimeSlot = (scheduleData, dayKey, hour) => {
  const daySchedule = scheduleData[dayKey] || [];
  const slotStartMinutes = hour * 60;
  const slotEndMinutes = (hour + 1) * 60;

  for (const show of daySchedule) {
    const showStartMinutes = toMinutes(show.startTime);
    const showEndMinutes = toMinutes(show.endTime);
    
    if (showEndMinutes <= showStartMinutes) {
      // Overnight show
      if (slotStartMinutes >= showStartMinutes || slotStartMinutes < showEndMinutes) {
        return show;
      }
    } else {
      if (showStartMinutes <= slotStartMinutes && showEndMinutes > slotStartMinutes) {
        return show;
      }
    }
  }
  
  return null;
};

// Show Art Component - separate component
const ShowArt = ({ show, isJukebox, jukeboxConfig }) => {
  const [imageError, setImageError] = useState(false);

  console.log("ShowArt render - isJukebox:", isJukebox, "jukeboxConfig:", jukeboxConfig);

  if (isJukebox) {
    // Try to use jukebox image if available and no error
    if (jukeboxConfig?.image && !imageError) {
      console.log("Attempting to load jukebox image:", jukeboxConfig.image);
      return (
        <div 
          className="show-art-container jukebox-art-container"
          style={jukeboxConfig.backgroundColor ? {
            background: jukeboxConfig.backgroundColor
          } : {}}
        >
          <img 
            src={jukeboxConfig.image} 
            alt={jukeboxConfig.title || "Jukebox"}
            className="show-art-image"
            onError={() => {
              console.log("Jukebox image failed to load, falling back to emoji");
              setImageError(true);
            }}
          />
        </div>
      );
    }

    // Fallback to emoji if no image or image error
    console.log("Using jukebox emoji fallback");
    return (
      <div 
        className="show-art-container jukebox-art-container"
        style={jukeboxConfig?.backgroundColor ? {
          background: jukeboxConfig.backgroundColor
        } : {}}
      >
        <span className="show-art-emoji">{jukeboxConfig?.art || '🎵'}</span>
      </div>
    );
  }

  // Regular show art
  if (show?.art && !imageError) {
    return (
      <div 
        className="show-art-container"
        style={show.backgroundColor ? {
          background: show.backgroundColor
        } : {}}
      >
        <img 
          src={show.art} 
          alt={show.title}
          className="show-art-image"
          onError={() => setImageError(true)}
        />
      </div>
    );
  }

  // Fallback: show initials
  return (
    <div 
      className="show-art-container"
      style={show?.backgroundColor ? {
        background: show.backgroundColor
      } : {}}
    >
      <span className="show-art-initials">
        {show?.title?.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() || 'SH'}
      </span>
    </div>
  );
};

// Timeline Schedule Component
const TimelineSchedule = ({ scheduleData, jukeboxConfig, selectedDay, currentTime, todayDayIndex }) => {
  const dayKey = selectedDay;
  const daySchedule = scheduleData[dayKey] || [];

  // Group consecutive hours with the same show
  const buildHourBlocks = () => {
    const blocks = [];
    let currentBlock = null;

    for (let hour = 0; hour < 24; hour++) {
      const show = getShowForTimeSlot(scheduleData, dayKey, hour);
      const showId = show ? show.id : 'jukebox';

      if (currentBlock && currentBlock.showId === showId && currentBlock.show === show) {
        currentBlock.endHour = hour;
      } else {
        if (currentBlock) blocks.push(currentBlock);
        currentBlock = {
          startHour: hour,
          endHour: hour,
          showId,
          show
        };
      }
    }
    if (currentBlock) blocks.push(currentBlock);

    return blocks;
  };

  const hourBlocks = buildHourBlocks();

  const formatHourDisplay = (hour) => {
    return `${hour.toString().padStart(2, '0')}:00`;
  };

  const isCurrentlyLive = (block) => {
    const now = currentTime;
    const currentDayIndex = (now.getDay() + 6) % 7; // Monday = 0
    
    // Only check if the selected day is today
    if (currentDayIndex !== DAYS.indexOf(dayKey)) return false;
    
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    const currentTimeInMinutes = currentHour * 60 + currentMinutes;
    
    // If it's a jukebox block (no show)
    if (!block.show) {
      // Check if this time block contains the current time
      const blockStartMinutes = block.startHour * 60;
      const blockEndMinutes = (block.endHour + 1) * 60;
      
      // Check that no show is scheduled during this time
      const hasShowDuringThisTime = daySchedule.some(show => {
        const showStartMinutes = toMinutes(show.startTime);
        const showEndMinutes = toMinutes(show.endTime);
        
        if (showEndMinutes <= showStartMinutes) {
          // Overnight show
          return currentTimeInMinutes >= showStartMinutes || currentTimeInMinutes < showEndMinutes;
        } else {
          return currentTimeInMinutes >= showStartMinutes && currentTimeInMinutes < showEndMinutes;
        }
      });
      
      return !hasShowDuringThisTime && 
             currentTimeInMinutes >= blockStartMinutes && 
             currentTimeInMinutes < blockEndMinutes;
    }
    
    // For show blocks, check if the current time falls within the show's actual schedule
    const show = block.show;
    const showStartMinutes = toMinutes(show.startTime);
    const showEndMinutes = toMinutes(show.endTime);
    
    if (showEndMinutes <= showStartMinutes) {
      // Overnight show (e.g., 23:00 - 02:00)
      return currentTimeInMinutes >= showStartMinutes || currentTimeInMinutes < showEndMinutes;
    } else {
      // Regular show
      return currentTimeInMinutes >= showStartMinutes && currentTimeInMinutes < showEndMinutes;
    }
  };

  return (
    <div className="timeline-schedule-container">
      <div className="schedule-list-header">
        <span>Shows on {DAY_LABELS[DAYS.indexOf(dayKey)]}</span>
      </div>
      <div className="timeline-list">
        {/* Single continuous vertical line */}
        <div className="timeline-continuous-line"></div>
        
        {hourBlocks.map((block) => {
          const isLive = isCurrentlyLive(block);
          const show = block.show;
          const isJukebox = !show;

          return (
            <div 
              key={`${block.startHour}-${block.endHour}`}
              className={`timeline-row ${isLive ? 'timeline-live' : ''}`}
            >
              {/* Time display on left */}
              <div className="timeline-time">
                <span className="time-start">{formatHourDisplay(block.startHour)}</span>
                <span className="time-separator">-</span>
                <span className="time-end">
                  {formatHourDisplay(block.endHour + 1)}
                </span>
              </div>

              {/* Thin separator - now just a spacer for the continuous line */}
              <div className="timeline-separator"></div>

              {/* Show content on right */}
              <div className={`timeline-content ${isJukebox ? 'jukebox-content' : 'show-content'}`}>
                <div className="content-layout">
                  {/* Show art */}
                  <ShowArt 
                    show={show} 
                    isJukebox={isJukebox} 
                    jukeboxConfig={jukeboxConfig}
                  />

                  {/* Show info */}
                  <div className="show-info">
                    <h4 className="show-title-text">
                      {isJukebox ? jukeboxConfig?.title || 'Jukebox' : show?.title}
                    </h4>
                    {!isJukebox && show?.host && (
                      <p className="show-host-text">
                        <i className="fas fa-user"></i> {show.host}
                      </p>
                    )}
                    {!isJukebox && show?.description && (
                      <p className="show-description-text">
                        {show.description}
                      </p>
                    )}
                    {isJukebox && (
                      <p className="show-description-text jukebox-description">
                        {jukeboxConfig?.description || 'Auto DJ - Playing the best music mix'}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Main SchedulePage Component
export const SchedulePage = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const dayIndex = day === 0 ? 6 : day - 1;
    return DAYS[dayIndex];
  });
  const { scheduleData, jukeboxConfig, isLoading } = useScheduleData();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get today's day index (0 = Monday, 6 = Sunday)
  const getTodayDayIndex = () => {
    const today = new Date();
    const day = today.getDay();
    return day === 0 ? 6 : day - 1;
  };

  const todayDayIndex = getTodayDayIndex();

  // Get date info for the next 7 days
  const getDayDates = () => {
    const today = new Date();
    const dates = [];
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push({
        day: date.getDate(),
        month: date.toLocaleString('default', { month: 'short' }),
        full: date
      });
    }
    
    return dates;
  };

  const dayDates = getDayDates();

  // Update current time every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  if (isLoading) {
    return (
      <div className="pageContainer">
        <section className="news-page-header">
          <h1><i className="fa-regular fa-clock"></i> Schedule</h1>
          <p>Loading schedule...</p>
        </section>
      </div>
    );
  }

  return (
    <>
      <div className="pageContainer">
        {/* Schedule Page Header */}
        <section className="news-page-header">
          <h1>
            Schedule
          </h1>
          <p>Stay updated with our weekly programming and never miss your favorite shows.</p>
        </section>

        {/* Consolidated Card with Day Selector and Timeline Schedule */}
        <div className="card-schedule">

          <div className="card-body">
            {/* Day Selector */}
            <div className="day-selector-section">
              {isMobile ? (
                /* Mobile: Use scrolling flexbox day-selector */
                <div className="day-selector">
                  {DAYS.map((dayKey, index) => {
                    const dateInfo = dayDates[index];
                    const isToday = index === todayDayIndex;
                    const isSelected = dayKey === selectedDay;
                    
                    return (
                      <button
                        key={dayKey}
                        className={`day-btn ${isSelected ? 'active' : ''} ${isToday ? 'today' : ''}`}
                        onClick={() => setSelectedDay(dayKey)}
                      >
                        <span className="day-name">{DAY_SHORT[index]}</span>
                        <span className="day-date">{dateInfo.day} {dateInfo.month}</span>
                      </button>
                    );
                  })}
                </div>
              ) : (
                /* Desktop: Use grid day-selector-grid */
                <div className="day-selector-grid">
                  {DAYS.map((dayKey, index) => {
                    const dateInfo = dayDates[index];
                    const isToday = index === todayDayIndex;
                    const isSelected = dayKey === selectedDay;
                    
                    return (
                      <button
                        key={dayKey}
                        className={`day-card-btn ${isSelected ? 'active' : ''} ${isToday ? 'today' : ''} ${index === 1 && !isToday ? 'tomorrow' : ''}`}
                        onClick={() => setSelectedDay(dayKey)}
                      >
                        <span className="day-card-name">{DAY_SHORT[index]}</span>
                        <span className="day-card-date">{dateInfo.day} {dateInfo.month}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Timeline Schedule */}
            <TimelineSchedule 
              scheduleData={scheduleData}
              jukeboxConfig={jukeboxConfig}
              selectedDay={selectedDay}
              currentTime={currentTime}
              todayDayIndex={todayDayIndex}
            />
          </div>
        </div>
      </div>
    </>
  );
};